import {Component, effect, inject} from '@angular/core';
import {NgTemplateOutlet} from '@angular/common';
import {MatDialogContent, MatDialogRef, MatDialogTitle} from '@angular/material/dialog';
import {MatExpansionModule} from '@angular/material/expansion';
import {TranslateModule} from '@ngx-translate/core';
import {WelcomeService} from '../welcome.service';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {iconToMaterialSymbol} from '../../shared/material-icons';
import {MatTooltip} from '@angular/material/tooltip';
import {Router} from '@angular/router';
import {MatDividerModule} from '@angular/material/divider';
import {OperationDef} from '../../core/definitions/operation-def';

interface OperationLink {
  path: string;
  queryParams: any;
}

interface OperationItem {
  label: string;
  icon?: string;
  tooltip?: string;
  link?: OperationLink;
  children?: OperationItem[];
}

interface OperationGroup {
  label: string;
  items: OperationItem[];
}

@Component({
    selector: 'app-create-object-dialog',
    imports: [
        NgTemplateOutlet,
        MatDialogTitle,
        MatDialogContent,
        TranslateModule,
        MatExpansionModule,
        MatButtonModule,
        MatIconModule,
        MatTooltip,
        MatDividerModule,
    ],
    templateUrl: './create-object-dialog.component.html',
    styleUrl: './create-object-dialog.component.scss'
})
export class CreateObjectDialogComponent {
  // Injections
  public dialogRef: MatDialogRef<CreateObjectDialogComponent> = inject(MatDialogRef);
  private readonly router: Router = inject(Router);
  private readonly welcomeService: WelcomeService = inject(WelcomeService);

  operationGroups: OperationGroup[] = [];

  constructor() {
    effect(() => {
      this.operationGroups = this.welcomeService.createNewOperations().map(operation => ({
        label: operation.menu_title,
        items: operation.sub_operations?.map(subOperation => this.toOperationItem(subOperation)) ?? []
      } satisfies OperationGroup));
    });
  }

  trackGroup = (_: number, group: OperationGroup) => group.label;

  trackItem = (_: number, item: OperationItem) => `${item.label}-${item.link?.path ?? ''}-${item.children?.length ?? 0}`;

  onLeafNodeClick($event: Event, node: OperationItem) {
    $event.preventDefault();

    if (!node.link) {
      return;
    }

    this.dialogRef.close();
    this.router.navigate([node.link.path], { queryParams: node.link.queryParams });
  }

  private toOperationItem(operation: OperationDef): OperationItem {
    const { objectType } = operation.operation_steps?.[0]?.change_state?.[0]?.state_params || {};
    let link: OperationLink | undefined = undefined;

    if (objectType) {
      link = {
        path: '/new',
        queryParams: {
          objectType,
        }
      };
    }

    const children = operation.sub_operations?.map(child => this.toOperationItem(child)) ?? [];

    return {
      label: operation.menu_title,
      icon: iconToMaterialSymbol(operation.menu_icon),
      tooltip: operation.menu_tooltip,
      link,
      ...(children.length ? { children } : {}),
    } satisfies OperationItem;
  }
}
